import { Divider, Headline, Layout, Text } from '@oneaudi/unified-web-alpha-components';
import { useEffect, useMemo } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import { PriceSectionFsag } from '~/app/pages/summary/dialogs/price-detail';
import { StyledPriceDetailContainer } from '~/app/pages/summary/dialogs/price-detail/core/styles/price-details-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { LayerType } from '~/app/tracking/feld-m-types';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';
import { Dialog } from '~/components/dialog';
import { useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';

export function PriceDetailsLegalFinlandDialog({ open, onClose }: DialogProps) {
	useEffect(() => {
		if (open) {
			appTrackingService.updateLayerComponent(LayerType.PriceDetailsLayer, 'open');
			appTrackingService.viewChangeEvent({
				eventName: `configurator - ${LayerType.PriceDetailsLayer}`,
				componentName: 'price-details-layer',
			});
		}
	}, [open]);
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<PriceDetailsDialogLegalFinlandContent />
		</Dialog>
	);
}

function PriceDetailsDialogLegalFinlandContent() {
	const { catalog, carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { priceDateRemark } = usePriceDetailRemarks();
	const { getReference } = useFootnotes();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const priceParts = useMemo(() => {
		return getFilteredPriceParts(priceSummary, config);
	}, [priceSummary?.parts, config.uiPreferences?.prices?.hiddenPriceParts]);

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const isBevModel = useMemo(() => {
		if (!carType) return undefined;
		return carType === 'BEV';
	}, [carType]);

	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	return (
		<StyledPriceDetailContainer>
			{}
			<div tabIndex={0} className="header-section">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'normal' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
					&nbsp;
					<FootnoteReferenceNgc
						reference={getReference(CAE_CONFIGUREDCAR_REMARK)}
						ariaLabel={i18n('ngc.a11y.footnotes.cae')}
					/>
				</Headline>
			</div>

			{}
			<div tabIndex={0} className="title-section">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{i18n('ngc.priceDetails.title')}</Text>
			</div>

			<ul className="price-list">
				{priceParts
					?.filter((p) => !(isBevModel && p.type.includes('taxableBenefit')))
					.map((part) => (
						<PriceSection
							key={part.type}
							pricePartType={part.type}
							title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
							price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
						/>
					))}
			</ul>

			<Divider />

			<PriceSectionFsag
				pricePartType="total"
				title={i18n('ngc.priceDetails.total')}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
				disclaimer={priceDisclaimerText}
			/>
			<ul className="price-list">
				{priceParts
					?.filter((p) => isBevModel && p.type.includes('taxableBenefit'))
					.map((part) => (
						<PriceSection
							key={part.type}
							pricePartType={part.type}
							title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
							price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
						/>
					))}
			</ul>
			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					{}
					<RenderHtml tabIndex={0} html={priceDateRemark} />
				</Text>
			)}
		</StyledPriceDetailContainer>
	);
}

function PriceSection({
	pricePartType,
	title,
	price,
	bold,
}: {
	pricePartType: string;
	/** Translated title of this section.  */
	title: string;
	/** Formatted price of this part. */
	price: string | null | undefined;
	bold?: boolean;
}) {
	const { i18n, i18nKeys } = useI18n();
	const { addFootnote, getReference } = useFootnotes();

	const i18nKey = `ngc.remarks.pricePart.${pricePartType}` as keyof typeof i18nKeys;

	if (i18nKey in i18nKeys) {
		addFootnote(i18nKey, i18n(i18nKey));
	}
	return (
		<Layout direction={{ default: 'column', md: 'row' }} columnGap="32px" justify="space-between">
			<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
				{title}

				<FootnoteReferenceNgc
					reference={getReference(i18nKey)}
					ariaLabel={i18n('ngc.a11y.footnotes.price.part', { part: title })}
				/>
			</Text>
			<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>{price}</Text>
		</Layout>
	);
}

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(): {
	priceDateRemark: string | undefined | null;
} {
	const { i18n, i18nKeys } = useI18n();

	return {
		priceDateRemark: 'ngc.summary.remark' in i18nKeys ? i18n('ngc.summary.remark') : null,
	};
}
